import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export const useHomeLibrary = (propertyId = null) => {
    const { user } = useAuth();
    const [libraryItems, setLibraryItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchLibraryItems = useCallback(async () => {
        if (!user || !propertyId) {
            setLoading(false);
            return;
        }

        let query = supabase
            .from("house_vault")
            .select("*")
            .eq("property_id", propertyId)
            .order("created_at", { ascending: false });

        const { data, error } = await query;

        if (error) {
            console.error("Error fetching library items:", error);
            toast({
                title: "Error",
                description: "Failed to load home library.",
                variant: "destructive"
            });
        } else {
            setLibraryItems(data || []);
        }
        setLoading(false);
    }, [user, propertyId]);

    const addLibraryItem = async (item) => {
        if (!user || !propertyId) return null;

        const { data, error } = await supabase
            .from("house_vault")
            .insert({
                user_id: user.id,
                property_id: propertyId,
                category: item.category,
                label: item.label,
                value: item.value || null,
                file_url: item.fileUrl || null,
                file_path: item.filePath || null,
            })
            .select()
            .single();

        if (error) {
            console.error("Error adding library item:", error);
            toast({
                title: "Error",
                description: "Failed to add to library.",
                variant: "destructive"
            });
            return null;
        }

        toast({
            title: "Item Added",
            description: `${item.label} has been saved to your library.`
        });

        await fetchLibraryItems();
        return data;
    };

    const deleteLibraryItem = async (itemId) => {
        if (!user) return false;

        const { error } = await supabase
            .from("house_vault")
            .delete()
            .eq("id", itemId);

        if (error) {
            console.error("Error deleting library item:", error);
            toast({
                title: "Error",
                description: "Failed to delete from library.",
                variant: "destructive"
            });
            return false;
        }

        setLibraryItems(prev => prev.filter(item => item.id !== itemId));
        return true;
    };

    useEffect(() => {
        let active = true;
        const timer = setTimeout(() => {
            if (active) {
                fetchLibraryItems();
            }
        }, 0);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [fetchLibraryItems]);

    return {
        libraryItems,
        loading,
        addLibraryItem,
        deleteLibraryItem,
        refetch: fetchLibraryItems
    };
};
